import React, { useState } from 'react';

const C = {
  bg: '#0a0a0c', bg2: '#111114', bg3: '#18181c',
  gold: '#c9a84c', goldL: '#e8c97a',
  sage: '#7aab8a', rose: '#c47a7a', sky: '#7a9ec4', lav: '#9a7ac4',
  border: 'rgba(201,168,76,0.15)',
  text: '#f0ede6', muted: 'rgba(240,237,230,0.5)', dim: 'rgba(240,237,230,0.25)',
};

const CATEGORIES = ['All', 'CSP', 'Covered Call', 'Wheel', 'Risk', 'Psychology'];

const SCENARIOS = [
  {
    id: 1, category: 'CSP', difficulty: 'Beginner',
    situation: "You sold a Cash-Secured Put on AAPL at the $180 strike for $2.10 credit. With 8 days to expiration, AAPL has dropped to $178 and your put is now worth $3.40. What do you do?",
    options: [
      { text: 'Hold — it will probably recover by expiration.', correct: false },
      { text: 'Buy back the put now and take the loss. Cut it early.', correct: false },
      { text: "Buy back the put for a loss now if you don't want to own shares — or let assignment happen if you're comfortable owning AAPL at $180.", correct: true },
      { text: 'Roll the put down to $170 to avoid assignment.', correct: false },
    ],
    explain: "This is the core Wheel decision point. Ask yourself: \"Am I comfortable owning 100 shares of AAPL at $180?\" If yes — let it happen. That's the plan. If no — you shouldn't have sold that put in the first place. Rolling down just moves your problem without solving it.",
    lynnNote: "I never sell a put on a stock I wouldn't be proud to own. That question — would I be happy owning this at my strike? — is the most important one in the whole strategy.",
  },
  {
    id: 2, category: 'Covered Call', difficulty: 'Beginner',
    situation: "You own 100 shares of KO at $60 cost basis. You sold a covered call at the $63 strike for $0.80. KO has now run up to $65 with 5 days to expiration. Your call is deep in the money. What do you do?",
    options: [
      { text: 'Buy back the call immediately to keep your shares.', correct: false },
      { text: "Let the call expire ITM — your shares get called away at $63, and you keep the $0.80 premium. That's the plan working.", correct: true },
      { text: 'Roll the call up to $67 to avoid assignment and capture more upside.', correct: false },
      { text: 'Buy more shares to hedge.', correct: false },
    ],
    explain: "When you sell a covered call, you agree to sell your shares at the strike if assigned. KO going to $65 when you sold the $63 call is actually the ideal outcome — you made money on the shares AND kept the premium. The \"missed\" upside above $63 is the cost of the strategy. It's not a loss.",
    lynnNote: "New traders panic when their covered call goes in the money. Don't. That's the trade working exactly as designed. You capped your upside — that was your choice when you sold the call.",
  },
  {
    id: 3, category: 'Risk', difficulty: 'Intermediate',
    situation: "You have a 0DTE SPX credit spread on. You sold the 5200/5195 put spread for $1.20. SPX has dropped and is now at 5197 with 2 hours left. Your spread is worth $2.80. What do you do?",
    options: [
      { text: 'Hold — SPX will recover. 2 hours is a long time.', correct: false },
      { text: 'Close the spread now for $2.80 and take the $1.60 loss per spread. Protect capital.', correct: true },
      { text: 'Add another spread below to average down.', correct: false },
      { text: 'Let it expire — worst case you lose $3.80 (spread width minus premium).', correct: false },
    ],
    explain: "On 0DTE, gamma is extreme. A spread that's $3 in the money with 2 hours left can spike to full loss in minutes. The rule: if your spread reaches 2x the premium received, close it. You received $1.20 — at $2.40 you close, no questions asked. Taking a $1.60 loss per spread is survivable. Letting a 0DTE trade run against you to expiration is how accounts get blown up.",
    lynnNote: "I have a strict 2x rule on 0DTE. When my spread hits double my credit, I close it. No negotiation with myself, no \"just a few more minutes.\" The rule exists because Past Lynn knew that Future Lynn would rationalize holding. Don't rationalize. Close it.",
  },
  {
    id: 4, category: 'Psychology', difficulty: 'Intermediate',
    situation: "You had a bad week — two trades went against you for a combined $1,400 loss. It's Monday morning and you feel the urge to put on three new trades to \"get it back\" quickly. What is happening and what should you do?",
    options: [
      { text: 'Trade more to recover faster. The market owes you a winning week.', correct: false },
      { text: 'This is revenge trading — a dangerous emotional state. Step away from the platform. Review your rules. Trade only if a setup genuinely qualifies.', correct: true },
      { text: 'Double your position size on your next trade to recover faster.', correct: false },
      { text: 'Take a put option on SPX to hedge everything.', correct: false },
    ],
    explain: "Revenge trading is one of the most common — and most destructive — patterns in options trading. The feeling of \"needing to get it back\" is your emotional brain overriding your trading plan. The market doesn't owe you anything. When you overtrade from this emotional state, you typically make your losses worse. The correct move is to stop, walk away, journal what happened, and only return to trading when you're approaching it analytically, not emotionally.",
    lynnNote: "I lost $75,000 in a single day early in my trading journey. Part of what made it so bad was that I kept trading to try to recover it. I made it worse. That experience is the reason I have hard daily loss limits now. When I hit my limit, I close the platform. Full stop.",
  },
  {
    id: 5, category: 'Wheel', difficulty: 'Intermediate',
    situation: "You've been running the Wheel on MSFT for 3 months. You sold a CSP, got assigned at $400, and have been selling covered calls. Your cost basis is now $383 after collecting premium. MSFT announces it's acquiring a company and the stock gaps up to $430 in pre-market. Your covered call is at $410. What do you do?",
    options: [
      { text: 'Close the covered call at a loss to keep the shares and capture the full $430 move.', correct: false },
      { text: "Hold your covered call position. Your shares will be called at $410 — you still made $27/share profit plus all the premium collected.", correct: true },
      { text: 'Sell more covered calls to double down.', correct: false },
      { text: 'Buy puts to hedge in case the gap reverses.', correct: false },
    ],
    explain: "Let's do the math: you own MSFT at $383 cost basis (after premium). Your covered call is at $410. If assigned, you sell at $410 — that's $27/share profit, or $2,700 on 100 shares, PLUS all the premium you collected over 3 months. This is an excellent outcome. Yes, you \"miss\" the move from $410 to $430 — but you capped your upside when you sold the call. That was your trade.",
    lynnNote: "The Wheel is not a strategy for capturing maximum upside. It's a strategy for consistent, compounding income. Every time I get called away at a profit, I consider it a win — even if the stock kept going. The goal is not perfection. The goal is consistency.",
  },
  {
    id: 6, category: 'CSP', difficulty: 'Advanced',
    situation: "You're looking at selling a CSP on NVDA. IV Rank is 72, the stock is at $875, and you're eyeing the $800 put for $8.50 credit. Earnings are in 19 days. Your intended DTE is 30 days. Should you place this trade?",
    options: [
      { text: 'Yes — IV Rank 72 means huge premium. This is a great opportunity.', correct: false },
      { text: "No — earnings are within 30 days. Never sell options you'll hold through an earnings event unless intentional.", correct: true },
      { text: 'Yes, but reduce position size to half.', correct: false },
      { text: "Yes — the $800 strike is far enough away to be safe.", correct: false },
    ],
    explain: "High IV Rank when earnings are approaching is a trap, not an opportunity. The elevated IV is BECAUSE of the upcoming earnings — the market is pricing in the uncertainty. After earnings, IV will collapse regardless of direction (this is called IV crush). Worse, the stock can gap 15–20% on earnings. Your \"far away\" $800 strike can become in-the-money overnight. Hard rule: no selling options you'll hold through earnings unless you're building an earnings-specific strategy with appropriate risk management.",
    lynnNote: "I've been tempted by high IV on earnings plays many times. The premium looks incredible. And then I remember: there's a reason it looks that incredible. The market is smarter than your desire for premium. Skip it.",
  },
  {
    id: 7, category: 'Risk', difficulty: 'Beginner',
    situation: "You have a $25,000 account. You want to sell a Cash-Secured Put on TSLA at the $250 strike. Selling this put requires $25,000 in buying power — your entire account. What's the problem?",
    options: [
      { text: "Nothing — if you have the buying power, you can sell the put.", correct: false },
      { text: "You'd be putting 100% of your capital into a single trade on a volatile stock. This violates basic risk management.", correct: true },
      { text: "TSLA is too expensive to trade on a $25,000 account.", correct: false },
      { text: "The strike is too far in the money.", correct: false },
    ],
    explain: "Never put more than 20–25% of your account into any single position. On a $25,000 account, that's $5,000–$6,250 max per trade. TSLA at the $250 strike requires $25,000 buying power for one contract — your entire account. If TSLA goes against you, you have no capital left to manage the position, add protective trades, or take other opportunities. Diversification across 4–6 positions protects you from any single trade destroying your account.",
    lynnNote: "Position sizing is where most beginners make their biggest mistakes. Not the strategy — the position size. I limit each trade to 5% of my account max on individual stocks, and never more than 20% of my portfolio in any single sector.",
  },
  {
    id: 8, category: 'Psychology', difficulty: 'Advanced',
    situation: "You've had an incredible month — up 18% in 30 days. You're feeling invincible. A friend tells you about a biotech stock with a FDA decision next week and IV Rank of 85. You're considering selling a put to \"add to a great month.\" What should you do?",
    options: [
      { text: "Go for it — you're on a hot streak and the premium is huge.", correct: false },
      { text: "Take a smaller position than normal since it's risky.", correct: false },
      { text: "Recognize this as overconfidence (euphoria). Apply your normal checklist. FDA binary events are a hard no. Pass on this trade.", correct: true },
      { text: "Ask your friend for more details and decide based on that.", correct: false },
    ],
    explain: "Euphoria after a winning streak is one of the most dangerous mental states for a trader. You feel like your judgment is better than it actually is, and you start taking risks you wouldn't normally take. The biotech/FDA play fails the checklist on multiple levels: binary event risk, IV inflated specifically because of the event, and the trade is sourced from a tip rather than your own analysis. The winning streak didn't improve your edge — apply the same standards as always.",
    lynnNote: "My worst trades have come right after my best months. The euphoria is real. I've learned to actually tighten my rules during winning streaks, not loosen them. A hot streak doesn't change the math of an individual trade.",
  },
];

export default function ScenarioLibrary({ onComplete, xpAward }) {
  const [category, setCategory]   = useState('All');
  const [activeId, setActiveId]   = useState(null);
  const [answers, setAnswers]     = useState({});
  const [revealed, setRevealed]   = useState({});
  const [completed, setCompleted] = useState(new Set());

  const filtered = SCENARIOS.filter(s => category === 'All' || s.category === category);
  const active   = SCENARIOS.find(s => s.id === activeId);

  const selectAnswer = (scenarioId, optIdx) => {
    if (revealed[scenarioId]) return;
    setAnswers(a => ({ ...a, [scenarioId]: optIdx }));
  };

  const reveal = (scenarioId) => {
    if (answers[scenarioId] == null) return;
    setRevealed(r => ({ ...r, [scenarioId]: true }));
    const newCompleted = new Set(completed);
    newCompleted.add(scenarioId);
    setCompleted(newCompleted);

    const xp = SCENARIOS.find(s => s.id === scenarioId)?.options[answers[scenarioId]]?.correct ? 60 : 20;
    xpAward && xpAward(xp);

    if (newCompleted.size === SCENARIOS.length) {
      const correct  = SCENARIOS.filter(s => newCompleted.has(s.id) && s.options[answers[s.id]]?.correct).length;
      const accuracy = Math.round((correct / SCENARIOS.length) * 100);
      onComplete && onComplete({ scenariosCompleted: newCompleted.size, accuracy });
    }
  };

  const diffColor = { Beginner: C.sage, Intermediate: C.sky, Advanced: C.lav };

  const navBtn = (active) => ({
    padding: '6px 14px', borderRadius: '7px', cursor: 'pointer',
    border: `0.5px solid ${active ? C.gold : C.border}`,
    background: active ? 'rgba(201,168,76,0.1)' : 'transparent',
    color: active ? C.goldL : C.muted, fontSize: '12px',
  });

  // ── List view ──────────────────────────────────────────────
  if (!activeId) return (
    <div style={{ fontFamily: "'Jost','Outfit',sans-serif", background: C.bg, color: C.text, minHeight: '100vh', padding: '28px 20px', maxWidth: '820px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.14em', color: C.gold, textTransform: 'uppercase', marginBottom: '6px' }}>♀ Trade Like a Woman</div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 500, margin: '0 0 4px' }}>Scenario Library</h2>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Real situations. Real decisions. What would you do?</p>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: '24px', marginBottom: '20px', padding: '14px 16px', background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '10px' }}>
        {[
          { label: 'COMPLETED', val: completed.size,   of: SCENARIOS.length,  color: C.gold },
          { label: 'CORRECT',   val: SCENARIOS.filter(s => completed.has(s.id) && s.options[answers[s.id]]?.correct).length, of: completed.size || null, color: C.sage },
          { label: 'XP EARNED', val: Array.from(completed).reduce((sum, id) => { const s = SCENARIOS.find(x => x.id === id); return sum + (s?.options[answers[id]]?.correct ? 60 : 20); }, 0), of: null, color: C.goldL },
        ].map(({ label, val, of, color }) => (
          <div key={label}>
            <div style={{ fontSize: '9px', color: C.dim, letterSpacing: '0.1em', marginBottom: '3px' }}>{label}</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '18px', color }}>
              {val}{of != null ? <span style={{ color: C.dim, fontSize: '13px' }}> / {of}</span> : ''}
            </div>
          </div>
        ))}
      </div>

      {/* Category filter */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '18px', flexWrap: 'wrap' }}>
        {CATEGORIES.map(c => (
          <button key={c} style={navBtn(category === c)} onClick={() => setCategory(c)}>{c}</button>
        ))}
      </div>

      {/* Scenario cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {filtered.map(s => {
          const done       = completed.has(s.id);
          const wasCorrect = done && s.options[answers[s.id]]?.correct;
          return (
            <div key={s.id}
              style={{ background: C.bg2, border: `0.5px solid ${done ? (wasCorrect ? 'rgba(122,171,138,0.3)' : 'rgba(196,122,122,0.2)') : C.border}`, borderRadius: '10px', padding: '16px 18px', cursor: 'pointer', transition: 'border-color 0.2s' }}
              onClick={() => setActiveId(s.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '4px', background: 'rgba(201,168,76,0.1)', color: C.goldL, letterSpacing: '0.06em' }}>{s.category}</span>
                    <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '4px', background: 'rgba(240,237,230,0.05)', color: diffColor[s.difficulty] || C.muted, letterSpacing: '0.06em' }}>{s.difficulty}</span>
                    {done && (
                      <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '4px', background: wasCorrect ? 'rgba(122,171,138,0.12)' : 'rgba(196,122,122,0.12)', color: wasCorrect ? C.sage : C.rose }}>
                        {wasCorrect ? '✓ Correct' : '✗ Incorrect'}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', lineHeight: 1.6, color: done ? C.muted : C.text }}>{s.situation.substring(0, 120)}...</div>
                </div>
                <div style={{ fontSize: '20px', color: done ? (wasCorrect ? C.sage : C.rose) : C.dim, flexShrink: 0 }}>
                  {done ? (wasCorrect ? '✓' : '✗') : '→'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Detail view ────────────────────────────────────────────
  const isRevealed  = revealed[active.id];
  const selectedOpt = answers[active.id];

  return (
    <div style={{ fontFamily: "'Jost','Outfit',sans-serif", background: C.bg, color: C.text, minHeight: '100vh', padding: '28px 20px', maxWidth: '820px', margin: '0 auto' }}>
      <button style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: C.muted, fontSize: '13px', cursor: 'pointer', marginBottom: '20px', padding: 0 }}
        onClick={() => setActiveId(null)}>
        ← Back to Scenarios
      </button>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '10px', padding: '3px 10px', borderRadius: '4px', background: 'rgba(201,168,76,0.1)', color: C.goldL, letterSpacing: '0.06em' }}>{active.category}</span>
        <span style={{ fontSize: '10px', padding: '3px 10px', borderRadius: '4px', background: 'rgba(240,237,230,0.05)', color: diffColor[active.difficulty] || C.muted, letterSpacing: '0.06em' }}>{active.difficulty}</span>
      </div>

      {/* Situation */}
      <div style={{ background: C.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '20px 22px', marginBottom: '20px' }}>
        <div style={{ fontSize: '10px', color: C.gold, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '10px' }}>The Situation</div>
        <div style={{ fontSize: '15px', lineHeight: 1.8, color: C.text }}>{active.situation}</div>
      </div>

      {/* Options */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>What do you do?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {active.options.map((opt, i) => {
            const selected = selectedOpt === i;
            let bg = 'transparent', border = C.border, color = C.text;
            if (isRevealed) {
              if (opt.correct)       { bg = 'rgba(122,171,138,0.1)'; border = 'rgba(122,171,138,0.4)'; color = C.sage; }
              else if (selected)     { bg = 'rgba(196,122,122,0.1)'; border = 'rgba(196,122,122,0.3)'; color = C.rose; }
              else                   { color = C.muted; }
            } else if (selected) {
              bg = 'rgba(201,168,76,0.08)'; border = 'rgba(201,168,76,0.4)'; color = C.goldL;
            }
            return (
              <div key={i}
                style={{ padding: '14px 16px', borderRadius: '9px', border: `0.5px solid ${border}`, background: bg, color, fontSize: '14px', cursor: isRevealed ? 'default' : 'pointer', lineHeight: 1.6, transition: 'all 0.15s' }}
                onClick={() => selectAnswer(active.id, i)}>
                <span style={{ fontFamily: "'DM Mono', monospace", marginRight: '10px', fontSize: '11px', opacity: 0.6 }}>{['A','B','C','D'][i]}</span>
                {opt.text}
                {isRevealed && opt.correct  && <span style={{ marginLeft: '8px' }}>✓</span>}
                {isRevealed && selected && !opt.correct && <span style={{ marginLeft: '8px' }}>✗</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Reveal button */}
      {!isRevealed && (
        <button
          style={{ width: '100%', padding: '13px', background: selectedOpt != null ? 'rgba(201,168,76,0.1)' : 'transparent', border: `0.5px solid ${selectedOpt != null ? 'rgba(201,168,76,0.4)' : C.border}`, borderRadius: '9px', color: selectedOpt != null ? C.goldL : C.muted, fontFamily: "'Jost', sans-serif", fontSize: '13px', letterSpacing: '0.08em', cursor: selectedOpt != null ? 'pointer' : 'not-allowed', marginBottom: '16px' }}
          onClick={() => reveal(active.id)}
          disabled={selectedOpt == null}>
          {selectedOpt == null ? 'Choose an answer first' : 'Reveal Answer & Explanation →'}
        </button>
      )}

      {/* Explanation */}
      {isRevealed && (
        <div>
          <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '10px', padding: '20px 22px', marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', color: C.gold, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '10px' }}>The Right Move</div>
            <div style={{ fontSize: '14px', lineHeight: 1.8, color: C.text }}>{active.explain}</div>
          </div>
          <div style={{ background: 'rgba(201,168,76,0.05)', border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '16px 20px', marginBottom: '20px' }}>
            <div style={{ fontSize: '10px', color: C.gold, letterSpacing: '0.1em', marginBottom: '8px' }}>LYNN SAYS</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '16px', fontStyle: 'italic', color: 'rgba(240,237,230,0.8)', lineHeight: 1.8 }}>"{active.lynnNote}"</div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={{ flex: 1, padding: '12px', background: 'transparent', border: `0.5px solid ${C.border}`, borderRadius: '8px', color: C.muted, fontFamily: "'Jost', sans-serif", fontSize: '13px', cursor: 'pointer' }}
              onClick={() => setActiveId(null)}>← All Scenarios</button>
            {(() => {
              const idx = SCENARIOS.findIndex(s => s.id === activeId);
              if (idx < SCENARIOS.length - 1) return (
                <button style={{ flex: 1, padding: '12px', background: 'rgba(201,168,76,0.08)', border: `0.5px solid rgba(201,168,76,0.3)`, borderRadius: '8px', color: C.goldL, fontFamily: "'Jost', sans-serif", fontSize: '13px', cursor: 'pointer' }}
                  onClick={() => setActiveId(SCENARIOS[idx + 1].id)}>
                  Next Scenario →
                </button>
              );
              return null;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
